import React, { useEffect, useState } from "react";
import { NotificationManager } from "src/util/notify";
import { useNavigate, useLocation } from "react-router";
import { useSelector } from "react-redux";
import InputKendoRct from "../../control-components/input/input";
import Loader from "../../control-components/loader/loader";
import { clinicService } from "../../services/clinicService";
import DatePickerKendoRct from "../../control-components/date-picker/date-picker";
import DropDownKendoRct from "../../control-components/drop-down/drop-down";
import APP_ROUTES from "../../helper/app-routes";
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { RadioGroup } from "@progress/kendo-react-inputs";
import NOTIFICATION_MESSAGE from "../../helper/notification-messages";
import { AnnouncementServices } from "../../services/announcementService";
import ApiUrls from "../../helper/api-urls";
import {Customeditor} from "../../control-components/custom-editor/custom-editor";
import ApiHelper from "../../helper/api-helper";
import { Error } from "@progress/kendo-react-labels";
import moment from "moment";

const AddAnnouncement = ({ onClose, selectedId }) => {
  const navigate = useNavigate();
  const regions = useSelector((state) => state.getRegion);
  const { state } = useLocation();
  const allClinic = useSelector((state) => state.getAllClinic);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [settingError, setSettingError] = useState(false);
  const [roles, setRoles] = useState([]);
  const [statesData, setStatesData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [fields, setFields] = useState({
    title: "",
    body: "",
    dateStart: new Date(),
    dateEnd: new Date(),
    isForEveryone: true,
    isClinicSpecific: false,
    isRoleSpecific: false,
    isStateSpecific: false,
    clinicIds: [],
    roleIds: [],
    stateIds: [],
    regionId: 1
  });

  const getFilteredClinics = () => {
    if (!allClinic || !fields.regionId) return [];
    const clinicsByRegion = allClinic.filter(clinic => clinic.regionId === fields.regionId);
  
    if (!searchTerm) return clinicsByRegion;
  
    return clinicsByRegion.filter(clinic =>
      clinic.clinicName.toLowerCase().startsWith(searchTerm.toLowerCase())
    );
  };
  
  const getFilteredStates = () => {
    if (!statesData) return [];
     
    if (!searchTerm) return statesData;
     
    return statesData.filter(state =>
      state.stateName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };
  
  const handleSearch = (event) => {
    setSearchTerm(event.filter.value);
  };
  
  const handleClinicSearch = (event) => {
    setSearchTerm(event.filter.value);
  };

  const handleRegionChange = (e) => {
    const newRegionId = e?.value?.id;

    setFields(prevFields => ({
      ...prevFields,
      regionId: newRegionId,
      clinicIds: [] 
    }));
  };
  const announcementTypes = [
    { label: 'For Everyone', value: 'isForEveryone' },
    { label: 'Clinic Specific', value: 'isClinicSpecific' },
    { label: 'Role Specific', value: 'isRoleSpecific' },
    { label: 'State Specific', value: 'isStateSpecific' }
  ];

  useEffect(() => {
    getRoles();
    getAllStates();
  
    if (state?.id) {
      getAnnouncementById(state.id);
    }
  }, [state]);

  const getAnnouncementById = async (id) => {
    try {
      setLoading(true);
      const response = await AnnouncementServices.getAnnouncementById(id);
      const announcement = response.resultData;
      
      setFields({
        title: announcement.title || "",
        body: announcement.body || "",
        dateStart: new Date(announcement.dateStart) || new Date(),
        dateEnd: new Date(announcement.dateEnd) || new Date(),
        isForEveryone: announcement.isForEveryone || false,
        isClinicSpecific: announcement.isClinicSpecific || false,
        isRoleSpecific: announcement.isRoleSpecific || false,
        isStateSpecific: announcement.isStateSpecific || false,
        clinicIds: announcement.clinicIds || [],
        roleIds: announcement.roleIds || [],
        stateIds: announcement.stateIds || [],
        regionId: announcement.regionId || 1
      });
      
      setLoading(false);
    } catch (error) {
      setLoading(false);
      NotificationManager.error(error.message || "Error fetching announcement");
    }
  };

  const getRoles = async () => {
    setLoading(true);
    ApiHelper.getRequest(ApiUrls.ROLES + 1)
      .then((result) => {
        setLoading(false);
        let roles = result.resultData;
        setRoles(roles);
      })
      .catch((error) => {
        NotificationManager.error(error.message);
      });
  };

  const getAllStates = async () => {
    setLoading(true);
    await clinicService
      .getAllClinicStates()
      .then((result) => {
        setStatesData(result.resultData)
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        NOTIFICATION_MESSAGE.error(error);
      });
  };

  const handleAnnouncementTypeChange = (e) => {
    const selectedType = e.value;
    
    const updatedFields = {
      ...fields,
      isForEveryone: false,
      isClinicSpecific: false,
      isRoleSpecific: false,
      isStateSpecific: false,
      clinicIds: [],
      roleIds: [],
      stateIds: [],
      [selectedType]: true
    };

    setFields(updatedFields);
  };

  const handleMultiSelect = (e, fieldName) => {
    const selectedValues = e?.value || [];
    setFields({
      ...fields,
      [fieldName]: selectedValues?.map(item => item.id)
    });
  };

  const handleSubmit = async () => {
    setSettingError(true);
    
    if (!handleValidation()) {
      return;
    }
    const submissionData = {
      title: fields.title.trim(),
      body: fields.body.trim(),
      dateStart: moment(fields.dateStart).format("M/D/YYYY"),
      dateEnd: moment(fields.dateEnd).format("M/D/YYYY"),
      isForEveryone: fields.isForEveryone,
      isClinicSpecific: fields.isClinicSpecific,
      isRoleSpecific: fields.isRoleSpecific,
      isStateSpecific: fields.isStateSpecific,
      clinicIds: fields.isClinicSpecific ? fields.clinicIds : [],
      roleIds: fields.isRoleSpecific ? fields.roleIds : [],
      stateIds: fields.isStateSpecific ? fields.stateIds : [],
      regionId: 0
    };

    try {
      setLoading(true);
      
      if (state) {
        
        await AnnouncementServices.updateAnnouncement(state?.id, submissionData);
        NotificationManager.success("Announcement updated successfully");
      } else {

        await AnnouncementServices.add_Announcement(submissionData);
        NotificationManager.success("Announcement created successfully");
       
      }
      
      handleCancel();
    } catch (error) {
      NotificationManager.error(error.message || "Error processing announcement");
      setLoading(false);
    }
  };

  const handleValidation = () => {
    let errors = {};
    let formIsValid = true;
    const dateStart = new Date(fields.dateStart);
    const dateEnd = new Date(fields.dateEnd);

    if (!fields.title.trim()) {
      formIsValid = false;
      errors.title = "Title is required";
    }

    if (!fields.body.trim()) {
      formIsValid = false;
      errors.body = "Body is required";
    }

    if (!fields.dateStart) {
      formIsValid = false;
      errors.dateStart = "Start date is required";
    }

    if (!fields.dateEnd) {
      formIsValid = false;
      errors.dateEnd = "End date is required";
    }



    if (
      fields.dateStart &&
      fields.dateEnd &&
      moment(fields.dateEnd).isBefore(moment(fields.dateStart))
    ) {
      formIsValid = false;
      errors.dateEndLessThanStart = "End date must be after or equal to start date";
    }

    const hasTypeSelected = fields.isForEveryone || 
                          fields.isClinicSpecific || 
                          fields.isRoleSpecific || 
                          fields.isStateSpecific;
    
    if (!hasTypeSelected) {
      formIsValid = false;
      errors.announcementType = "Please select announcement type";
    }

    if (fields.isClinicSpecific && fields.clinicIds.length === 0) {
      formIsValid = false;
      errors.clinicIds = "Please select at least one clinic";
    }

    if (fields.isRoleSpecific && fields.roleIds.length === 0) {
      formIsValid = false;
      errors.roleIds = "Please select at least one role";
    }

    if (fields.isStateSpecific && fields.stateIds.length === 0) {
      formIsValid = false;
      errors.stateIds = "Please select at least one state";
    }

    setErrors(errors);
    return formIsValid;
  };

  const handleCancel = () => {
    navigate(APP_ROUTES.ANNOUNCEMENT);
  };

  const handleTextChange = (props) => {
    const sanitizeContent = (html) => {
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = html.trim();
      const elementsWithAttributes = tempDiv.querySelectorAll("*");
      elementsWithAttributes.forEach((el) => {
        el.removeAttribute("style");
        el.removeAttribute("class");
      });
 
      return tempDiv.textContent?.trim() === "" ? "" : tempDiv.innerHTML;
    };
 
    const cleanedContent = sanitizeContent(props.html);
 
    setFields({
      ...fields,
      body: cleanedContent,
    });
 
  };


  const handleChange = (e) => {
    const name = e.target.name;
    let value = e.target.value;

    if (e.target && e.target.type === 'checkbox') {
      value = e.target.checked;
    }

    

    if (e.target.props && e.target.props.textField) {
      value = { 
        id: e.target.value.id,
        [e.target.props.textField]: e.target.value[e.target.props.textField]
      };
    }

    setFields(prevFields => ({
      ...prevFields,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: ''
      }));
    }
  };



  return (
    <>
      <div className="client-accept notenetic-container">
        <div className="row">
          <div className="mb-3 col-lg-6 col-md-6 col-12">
            <InputKendoRct
              value={fields.title}
              onChange={handleChange}
              name="title"
              label="Title"
              validityStyles={settingError}
              className="form-control"
              placeholder="Title"
            />
            <Error>{errors.title}</Error>
          </div>
          <div className="mb-3 col-lg-6 col-md-6 col-12">
          <Customeditor
             value={fields.body}
             onChange={handleTextChange}
             height={100}
             label={"Body"}
            />
            <Error>{errors.body}</Error>
          </div>

          <div className="mb-3 col-lg-6 col-md-6 col-12 date-field">
            <DatePickerKendoRct
              onChange={handleChange}
              value={fields.dateStart}
              name="dateStart"
              label="Start Date"
              error={errors.dateStart}
              required={true}
              validityStyles={settingError}
              // min={fields.dateStart}
            />
          </div>

          <div className="mb-3 col-lg-6 col-md-6 col-12 date-field">
            <DatePickerKendoRct
              onChange={handleChange}
              value={fields.dateEnd}
              name="dateEnd"
              label="End Date"
              required={true}
              min={fields.dateEnd}
            />
             <Error>{errors.dateEnd || errors.dateEndLessThanStart}</Error>
          </div>
          

          <div className="mb-3 col-12">
            <label className="form-label">Announcement Type</label>
            <RadioGroup
              data={announcementTypes}
              value={Object.keys(fields).find(key => fields[key] === true)}
              onChange={handleAnnouncementTypeChange}
              layout="horizontal"
            />
          </div>

          {fields.isClinicSpecific && (
            <>
            <div className="mb-3 col-lg-12">
              
            <DropDownKendoRct
              validityStyles={settingError}
              label="Region"
              onChange={handleRegionChange}
              data={regions}
              value={regions.find(r => r.id === fields.regionId)}
              textField="databaseName"
              dataItemKey="id"
              name="regionId"
              required={true}
            />
            </div>
          <div className="mb-3 col-lg-12 edit-Service-popup">

                      <MultiSelect
                        data={getFilteredClinics()}
                        textField="clinicName"
                        dataItemKey="id"
                        value={getFilteredClinics().filter(clinic => 
                          fields.clinicIds.includes(clinic.id)
                        )}
                        onChange={(e) => handleMultiSelect(e, 'clinicIds')}
                        placeholder="Search and select clinics..."
                        label="Select Clinics"
                        error={errors.clinicIds}
                        filterable={true}
                        onFilterChange={handleSearch}
                        validityStyles={settingError}
                        virtual={false}
                      />
                      <Error>{errors.clinicIds}</Error>
          </div>
          </>
          )}

          {fields.isRoleSpecific && (
            <div className="mb-3 col-lg-12 edit-Service-popup">
              <MultiSelect
                data={roles}
                textField="roleName"
                dataItemKey="id"
                value={roles.filter(role => fields.roleIds.includes(role.id))}
                onChange={(e) => handleMultiSelect(e, 'roleIds')}
                placeholder="Select Roles"
                label="Select Roles"
                error={errors.roleIds}
                virtual={false}
              />
              <Error>{errors.roleIds}</Error>
            </div>
          )}

        {fields.isStateSpecific && (
          <div className="mb-3 col-lg-12 edit-Service-popup">
            <MultiSelect
              data={getFilteredStates()}
              textField="stateName"
              dataItemKey="id"
              value={statesData?.filter(state => 
                fields?.stateIds?.includes(state?.id)
              )}
              onChange={(e) => handleMultiSelect(e, 'stateIds')}
              placeholder="Search and select states..."
              label="Select States"
              error={errors.stateIds}
              virtual={false}
              filterable={true}
              onFilterChange={handleSearch}
              validityStyles={settingError}
            />
        <Error>{errors.stateIds}</Error>
          </div>
        )}
        </div>
      </div>

      {loading && <Loader />}

      <div className="d-flex my-4">
        <button 
          className="green-btn btn-primary mr-3 btn-space" 
          onClick={handleSubmit}
        >
          {state ? "Update" : "Add"}
        </button>
        <button 
          className="btn grey-secondary btn-space" 
          onClick={handleCancel}
        >
          Cancel
        </button>
      </div>
    </>
  );
};

export default AddAnnouncement;